import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        // Check localStorage first, then system preference
        const stored = localStorage.getItem('gymtracker-theme');
        if (stored) return stored;

        // Check system preference
        if (window.matchMedia?.('(prefers-color-scheme: light)').matches) {
            return 'light';
        }
        return 'dark';
    });

    // Apply theme to document
    useEffect(() => {
        const root = document.documentElement;

        if (theme === 'light') {
            root.classList.add('light-theme');
            root.classList.remove('dark-theme');
            document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#f5f5f5');
        } else {
            root.classList.add('dark-theme');
            root.classList.remove('light-theme');
            document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#DC2626');
        }

        localStorage.setItem('gymtracker-theme', theme);
    }, [theme]);

    // Listen for system theme changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
        const handleChange = (e) => {
            const stored = localStorage.getItem('gymtracker-theme');
            // Only auto-switch if user hasn't manually set a preference
            if (!stored) {
                setTheme(e.matches ? 'light' : 'dark');
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme(prev => prev === 'dark' ? 'light' : 'dark');
    }, []);

    const setDark = useCallback(() => setTheme('dark'), []);
    const setLight = useCallback(() => setTheme('light'), []);

    const value = {
        theme,
        isDark: theme === 'dark',
        isLight: theme === 'light',
        toggleTheme,
        setDark,
        setLight
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeContext;
